import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd } from '@theme/utilities';

/**
 * メインメニューのドロワーを管理するカスタム要素です。
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - details 要素
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details'];

  connectedCallback() {
    super.connectedCallback();
    // キー操作時のイベントリスナーを追加
    this.addEventListener('keyup', this.#onKeyUp);
    // アニメーションが完了した際のイベントリスナー設定
    this.#setupAnimatedElementListeners();
    // summaryクリック時のカスタムイベント設定
    this.#setupSummaryClick();
    // スティッキーヘッダーの状態を監視
    this.#observeStickyState();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    // イベントリスナーを削除
    this.removeEventListener('keyup', this.#onKeyUp);
  }

  /**
   * キーアップイベントを処理し、Escapeキーが押された場合にドロワーを閉じます。
   * @param {KeyboardEvent} event - キーボードイベント
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;  // Escapeキーでのみ反応
    this.#close(this.refs.details);  // ドロワーを閉じる
  };

  /**
   * ドロワーが開いているかどうかを判定します。
   * @returns {boolean} - ドロワーが開いていれば true、それ以外は false
   */
  get isOpen() {
    return this.refs.details.classList.contains('menu-open');
  }

  /**
   * ドロワーを開いたり閉じたりするトグル処理を行います。
   */
  toggle() {
    this.isOpen ? this.close() : this.open();
  }

  /**
   * ドロワーを開く処理を行います。
   */
  open() {
    const details = this.refs.details;
    const summary = details.querySelector('summary');
    if (!summary || this.isOpen) return;  // すでに開いている場合は何もしない

    // 初回に open 属性を追加
    details.setAttribute('open', '');
    summary.setAttribute('aria-expanded', 'true');  // アクセシビリティ対応

    // 再描画後にクラスを追加
    requestAnimationFrame(() => {
      details.classList.add('menu-open');
      // フォーカスをトラップ
      setTimeout(() => trapFocus(details), 0);
    });
  }

  /**
   * ドロワーを閉じる処理を行います。
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * ドロワーを閉じ、必要なクリーンアップを行います。
   * @param {HTMLDetailsElement} details - 閉じる対象の details 要素
   */
  #close(details) {
    const summary = details.querySelector('summary');
    if (!summary) return;  // summary がない場合は何もしない

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');

    // アニメーション終了後のクリーンアップ処理
    let cleaned = false;
    const cleanup = () => {
      if (cleaned) return;  // すでにクリーンアップされている場合は何もしない
      cleaned = true;
      reset(details);  // styles と attributes をリセット
      if (details === this.refs.details) removeTrapFocus();  // フォーカスのトラップ解除
    };

    // アニメーション終了後に cleanup を実行
    onAnimationEnd(details, cleanup);
    // タイムアウトによるフォールバック
    setTimeout(cleanup, 500);
  }

  /**
   * アニメーションがある要素に 'will-change' プロパティの解除イベントリスナーを設定します。
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((el) => {
      const removeWillChangeOnAnimationEnd = (event) => {
        // アニメーション終了後に 'will-change' をリセット
        event.target.style.setProperty('will-change', 'unset');
        el.removeEventListener('animationend', removeWillChangeOnAnimationEnd);
      };
      el.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * summary 要素のクリックイベントを設定し、ネイティブのトグル動作を無効化します。
   */
  #setupSummaryClick() {
    const summary = this.refs.details.querySelector('summary');
    if (!summary) return;

    summary.addEventListener('click', (e) => {
      e.preventDefault();  // ネイティブのトグル動作を無効化
      e.stopImmediatePropagation();  // 他のトグル処理を無効化
      this.toggle();  // カスタムトグル処理
    });
  }

  /**
   * スティッキーヘッダーの状態を監視し、メニューの表示位置を調整します。
   */
  #observeStickyState() {
    const stickyHeader = document.querySelector('.header-section');
    if (!stickyHeader) return;

    const handleStickyState = () => {
      const isSticky = stickyHeader.classList.contains('is-sticky');
      const details = this.refs.details;

      // ヘッダーがスティッキーの場合、メニューの位置を調整
      if (isSticky && !details.classList.contains('menu-open')) {
        details.style.top = `var(--header-total-height)`;
      } else {
        details.style.top = 'auto';
      }
    };

    // スクロールとリサイズ時にメニュー位置を調整
    window.addEventListener('scroll', handleStickyState);
    window.addEventListener('resize', handleStickyState);

    // 初期状態でメニュー位置を調整
    handleStickyState();
  }
}

// カスタムエレメントが未定義であれば定義する
if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

// ---------------- Utilities ----------------

/**
 * ドロワーの状態を初期状態にリセットします。
 * @param {HTMLDetailsElement} details - リセット対象の details 要素
 */
function reset(details) {
  details.classList.remove('menu-open');
  details.removeAttribute('open');
  details.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}

/**
 * 指定した要素のビューポート内での表示高さを返します。
 * @param {HTMLElement} element - 表示高さを取得する要素
 * @returns {number} - ビューポート内での表示高さ（ピクセル単位）
 */
function getVisibleHeight(element) {
  if (!element) return 0;
  const rect = element.getBoundingClientRect();
  const windowHeight = window.innerHeight || document.documentElement.clientHeight;
  const visibleTop = Math.max(rect.top, 0);
  const visibleBottom = Math.min(rect.bottom, windowHeight);
  return Math.max(0, visibleBottom - visibleTop);
}

/**
 * ヘッダーの総高さ（アナウンスメントとヘッダーを含む）を更新します。
 */
function updateHeaderHeight() {
  const announcement = document.querySelector('.announcement-bar'); 
  const header = document.querySelector('.header-section'); 

  const announcementHeight = getVisibleHeight(announcement);
  const headerHeight = header?.offsetHeight || 0;

  const total = announcementHeight + headerHeight;

  const current = getComputedStyle(document.documentElement)
    .getPropertyValue('--header-total-height')
    .trim();

  if (current !== total + 'px') {
    document.documentElement.style.setProperty('--header-total-height', total + 'px');
  }
}


// 初期実行でヘッダー高さを設定
updateHeaderHeight();

// リサイズ時にヘッダー高さを更新
window.addEventListener('resize', updateHeaderHeight);

// DOMの変化を監視し、必要に応じてヘッダー高さを更新
const observer = new MutationObserver(updateHeaderHeight);
observer.observe(document.body, { attributes: true, childList: true, subtree: true });
